import React, { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { searchAPI, documentsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { Search, FileText, Download, Eye, Filter, X, ExternalLink, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const SearchPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    module_id: '', document_type: '', status: '',
    vendor: '', document_number: '',
    min_amount: '', max_amount: '',
    start_date: '', end_date: '', client_id: '',
  });

  const tenantId = user?.tenant_id || 'default';

  const doSearch = useCallback(async (p = 1) => {
    setLoading(true);
    setSearched(true);
    try {
      const params = { q: query, tenant_id: tenantId, page: p, per_page: 20 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await searchAPI.search(params);
      setResults(data.results || []);
      setTotal(data.total || 0);
      setPage(p);
    } catch (err) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  }, [query, tenantId, filters]);

  const handleKeyDown = (e) => { if (e.key === 'Enter') doSearch(1); };

  const handleDownload = async (docId) => {
    try {
      const { data } = await documentsAPI.getFile(docId);
      if (data.url) window.open(data.url, '_blank');
    } catch { toast.error('Download failed'); }
  };

  const clearFilters = () => setFilters({ module_id: '', document_type: '', status: '', vendor: '', document_number: '', min_amount: '', max_amount: '', start_date: '', end_date: '', client_id: '' });
  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  const totalPages = Math.ceil(total / 20);

  const statusBadge = (s) => {
    const map = { pending: 'bg-slate-100 text-slate-700', processing: 'bg-blue-100 text-blue-700', ocr_complete: 'bg-emerald-100 text-emerald-700', needs_review: 'bg-amber-100 text-amber-700', approved: 'bg-emerald-100 text-emerald-700', rejected: 'bg-red-100 text-red-700', failed: 'bg-red-100 text-red-700' };
    return <Badge className={map[s] || 'bg-slate-100 text-slate-700'}>{s}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="search-page">
      <div><h1 className="text-3xl font-semibold text-slate-900">Search</h1><p className="text-slate-500 mt-1">Search across all documents, OCR text, and extracted data</p></div>

      {/* Search Bar */}
      <div className="flex gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <Input className="pl-10 h-12 text-base" placeholder="Search documents, vendors, amounts..." value={query} onChange={e => setQuery(e.target.value)} onKeyDown={handleKeyDown} data-testid="search-input" />
        </div>
        <Button className="h-12 px-6" onClick={() => doSearch(1)} data-testid="search-btn">Search</Button>
        <Button variant="outline" className="h-12" onClick={() => setShowFilters(!showFilters)}>
          <Filter className="h-4 w-4" />{activeFilterCount > 0 && <Badge className="ml-2 bg-indigo-500 text-white text-xs">{activeFilterCount}</Badge>}
        </Button>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card className="p-4">
          <div className="flex justify-between items-center mb-4"><h3 className="font-medium text-sm text-slate-700">Advanced Filters</h3>{activeFilterCount > 0 && <Button variant="ghost" size="sm" onClick={clearFilters}><X className="mr-1 h-3 w-3" />Clear</Button>}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-1"><Label className="text-xs">Module</Label><Select value={filters.module_id || 'all_m'} onValueChange={v => setFilters(p => ({...p, module_id: v === 'all_m' ? '' : v}))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all_m">All</SelectItem><SelectItem value="timesheet_ocr">Timesheet</SelectItem><SelectItem value="accounting_intake">Accounting</SelectItem></SelectContent></Select></div>
            <div className="space-y-1"><Label className="text-xs">Type</Label><Select value={filters.document_type || 'all_t'} onValueChange={v => setFilters(p => ({...p, document_type: v === 'all_t' ? '' : v}))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all_t">All</SelectItem><SelectItem value="timesheet">Timesheet</SelectItem><SelectItem value="receipt">Receipt</SelectItem><SelectItem value="invoice">Invoice</SelectItem><SelectItem value="contract">Contract</SelectItem></SelectContent></Select></div>
            <div className="space-y-1"><Label className="text-xs">Status</Label><Select value={filters.status || 'all_s'} onValueChange={v => setFilters(p => ({...p, status: v === 'all_s' ? '' : v}))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all_s">All</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="processing">Processing</SelectItem><SelectItem value="ocr_complete">Complete</SelectItem><SelectItem value="needs_review">Needs Review</SelectItem><SelectItem value="approved">Approved</SelectItem></SelectContent></Select></div>
            <div className="space-y-1"><Label className="text-xs">Vendor</Label><Input placeholder="Vendor name" value={filters.vendor} onChange={e => setFilters(p => ({...p, vendor: e.target.value}))} /></div>
            <div className="space-y-1"><Label className="text-xs">Doc Number</Label><Input placeholder="Invoice/Receipt #" value={filters.document_number} onChange={e => setFilters(p => ({...p, document_number: e.target.value}))} /></div>
            <div className="space-y-1"><Label className="text-xs">Min Amount</Label><Input type="number" placeholder="0.00" value={filters.min_amount} onChange={e => setFilters(p => ({...p, min_amount: e.target.value}))} /></div>
            <div className="space-y-1"><Label className="text-xs">Max Amount</Label><Input type="number" placeholder="0.00" value={filters.max_amount} onChange={e => setFilters(p => ({...p, max_amount: e.target.value}))} /></div>
            <div className="space-y-1"><Label className="text-xs">Upload Date From</Label><Input type="date" value={filters.start_date} onChange={e => setFilters(p => ({...p, start_date: e.target.value}))} /></div>
          </div>
        </Card>
      )}

      {/* Results */}
      {loading ? <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
      : !searched ? <Card className="py-16"><CardContent className="text-center"><Search className="h-16 w-16 text-slate-300 mx-auto mb-4" /><h3 className="text-xl font-medium text-slate-900">Search your documents</h3><p className="text-slate-500 mt-2">Enter keywords, vendor names, amounts, or document numbers</p></CardContent></Card>
      : results.length === 0 ? <Card className="py-16"><CardContent className="text-center"><FileText className="h-16 w-16 text-slate-300 mx-auto mb-4" /><h3 className="text-xl font-medium text-slate-900">No results</h3><p className="text-slate-500 mt-2">Try different keywords or adjust filters</p></CardContent></Card>
      : (
        <Card>
          <CardHeader className="pb-3"><div className="flex justify-between items-center"><CardTitle className="text-base">{total} result{total !== 1 && 's'}</CardTitle><Button variant="ghost" size="sm" onClick={() => doSearch(page)}><RefreshCw className="h-4 w-4" /></Button></div></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Document</TableHead><TableHead>Module / Type</TableHead><TableHead>Extracted Info</TableHead><TableHead>Confidence</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {results.map((r, i) => (
                  <TableRow key={r.document_id || i}>
                    <TableCell>
                      <div className="max-w-[200px]"><p className="font-medium text-sm truncate">{r.document?.original_filename || r.filename}</p><p className="text-xs text-slate-500">{r.document?.company_id ? `Client: ${r.document.company_id.slice(0, 8)}` : ''}</p></div>
                    </TableCell>
                    <TableCell><div><span className="text-sm capitalize">{r.document?.module_id?.replace(/_/g, ' ') || r.module_id}</span><br/><span className="text-xs text-slate-500 capitalize">{r.document?.document_type || r.document_type}</span></div></TableCell>
                    <TableCell className="max-w-[250px]">
                      {r.extracted_fields && Object.keys(r.extracted_fields).length > 0 ? (
                        <div className="text-xs space-y-0.5">{Object.entries(r.extracted_fields).slice(0, 3).map(([k, v]) => <div key={k}><span className="text-slate-500 capitalize">{k.replace(/_/g, ' ')}:</span> <span className="font-medium">{String(v).slice(0, 40)}</span></div>)}</div>
                      ) : r.raw_text_preview ? (
                        <p className="text-xs text-slate-500 truncate max-w-[250px]">{r.raw_text_preview}</p>
                      ) : '-'}
                    </TableCell>
                    <TableCell>{r.confidence != null || r.document?.confidence_score != null ? <span className={`font-mono text-sm ${(r.confidence || r.document?.confidence_score || 0) >= 0.85 ? 'text-emerald-600' : 'text-amber-600'}`}>{((r.confidence || r.document?.confidence_score || 0) * 100).toFixed(0)}%</span> : '-'}</TableCell>
                    <TableCell>{statusBadge(r.document?.status || r.status)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/documents`)} title="View"><Eye className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDownload(r.document_id)} title="Download"><Download className="h-4 w-4" /></Button>
                        {(r.document?.status === 'needs_review') && <Button variant="ghost" size="sm" onClick={() => navigate('/review-queue')} title="Review"><ExternalLink className="h-4 w-4" /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 p-4 border-t">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => doSearch(page - 1)}>Prev</Button>
                <span className="text-sm text-slate-500">Page {page} of {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => doSearch(page + 1)}>Next</Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SearchPage;
